import { Megaphone } from 'lucide-react'
import { useLayoutEffect, useRef, useState } from 'react'

import { useTheme } from '../theme/ThemeProvider'
import { readableAlertColor } from './alert-color'
import {
  useClinicOrganizationAlert,
  type OrganizationAlertReader,
} from './organization-alert-queries'
import styles from './OrganizationAlertBanner.module.css'

/**
 * The selected clinic's Organization alert, under the header row on every
 * page. Nothing renders while there is no active alert, while it loads, or if
 * it could not be read: the header never shows a placeholder or an error.
 */
export function OrganizationAlertBanner({
  tenantId,
  reader,
  onHeightChange,
}: {
  tenantId: string | null
  reader?: OrganizationAlertReader
  /** The banner's rendered height in px, 0 while nothing is shown. */
  onHeightChange?: (height: number) => void
}) {
  const alert = useClinicOrganizationAlert(tenantId, reader)
  useLayoutEffect(() => {
    if (!alert) onHeightChange?.(0)
  }, [alert, onHeightChange])
  if (!alert) return null
  // A new clinic or a new revision starts collapsed again.
  return (
    <AlertLine
      key={`${tenantId}:${alert.revision}`}
      message={alert.message}
      color={alert.color}
      onHeightChange={onHeightChange}
    />
  )
}

/**
 * The message is clamped to two lines (three on phones). Show more appears
 * only when the clamped text is actually cut off, and sits outside the live
 * region so toggling it is not announced as a new alert.
 */
function AlertLine({
  message,
  color,
  onHeightChange,
}: {
  message: string
  color: string
  onHeightChange?: (height: number) => void
}) {
  const { theme } = useTheme()
  const [expanded, setExpanded] = useState(false)
  const [clipped, setClipped] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const text = useRef<HTMLParagraphElement>(null)
  const textColor = readableAlertColor(color, theme)

  // Measured after every render, then again when a width change reflows it.
  useLayoutEffect(() => {
    const banner = box.current
    const element = text.current
    if (!banner || !element) return
    const measure = () => {
      if (!expanded) setClipped(element.scrollHeight > element.clientHeight + 1)
      onHeightChange?.(banner.getBoundingClientRect().height)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(banner)
    observer.observe(element)
    return () => observer.disconnect()
  }, [expanded, message, clipped, onHeightChange])

  return (
    <div ref={box} className={styles.banner}>
      <div className={styles.content} role="status">
        <span
          className={styles.icon}
          style={{ color: textColor }}
          aria-hidden="true"
        >
          <Megaphone size={15} strokeWidth={2} />
        </span>
        <p
          ref={text}
          className={styles.message}
          style={{ color: textColor }}
          data-collapsed={expanded ? undefined : true}
        >
          <span className={styles.srOnly}>Organization alert: </span>
          {message}
        </p>
      </div>
      {expanded || clipped ? (
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={expanded}
          onClick={() => setExpanded((value) => !value)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      ) : null}
    </div>
  )
}
